<template>
  <div id="TMH">
    <div v-if="isGetSurveyLoading" class="d-flex justify-content-center">
      <b-spinner class="brand-primary"></b-spinner>
    </div>
    <SelectCoach v-else-if="isSelectCoachScreen" :handleBtnClick="returnToSurvey"/>
    <ReferToEAP
      v-else-if="isReferToEAPScreen"
      :pageContent="redirectOptions['Refer User to EAP']"
    />
    <ProceedToSurvey
      v-else-if="isProceedToSurveyScreen"
      :pageContent="redirectOptions['Administer CORE-10']"
      :handleBtnClick="proceedToNextSurvey"
    />
    <div v-else>
      <h2 class="dmh-start-title brand-text">{{ currentQuestion.text }}</h2>
      <button
        v-for="option in currentQuestion.options"
        :key="option.id"
        @click="selectOption(option)"
        class="list-item"
        :class="{'active' : selectedOption === option}"
      >
      {{option.text}}
     </button>
      <div
        class="d-flex justify-content-end align-items-center pt-5 btn-container"
      >
        <b-button
          variant="cta-outline"
          size="lg"
          class="py-4 px-5"
          @click="showPreviousQuestion()"
        >
          Previous
        </b-button>
        <b-button
          variant="cta"
          size="lg"
          :disabled="!selectedOption || isAnswerSurveyLoading"
          @click="showNextQuestion()"
        >
          Continue
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import config from "../../config/constants";
import SelectCoach from "./SelectCoach.vue";
import ReferToEAP from "./ReferToEAP.vue";
import ProceedToSurvey from "./ProceedToSurvey.vue";

export default {
  name: "TmhJourney",
  components: {
    SelectCoach,
    ReferToEAP,
    ProceedToSurvey
  },
  props: {
    previous: {
      type: Function,
      required: true,
      default: () => {}
    },
    questionHistory: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      questionList: [],
      questionsCount: null,
      currentQuestionIndex: 0,
      selectedOption: null,
      answers: [],
      isGetSurveyLoading: false,
      isAnswerSurveyLoading: false,
      questionnaireResult: null,
      showProceedToSurveyScreen: false,
      showSelectCoachScreen: true,
      surveyId: null,
    };
  },
  mounted() {
    this.getFixedSurvey();
  },
  computed: {
    ...mapState(["handoverPage"]),
    currentQuestion() {
      return this.questionList[this.currentQuestionIndex];
    },
    isSelectCoachScreen() {
      return this.questionnaireResult === "Commence Coaching Program" && this.showSelectCoachScreen;
    },
    isReferToEAPScreen() {
      return this.questionnaireResult === "Refer User to EAP";
    },
    isProceedToSurveyScreen() {
      return this.questionnaireResult === "Administer CORE-10" || this.showProceedToSurveyScreen;
    },
    redirectOptions() {
      const serviceType = this.handoverPage?.service_type === 'slm' ? 'Sleep' : 'Mental Health';

      const dynamicRedirectOptions = {
        "Refer User to EAP": {
          title: "Thank you for answering these questions.",
          subtitle:
            `We really appreciate your time and patience. Based on your answers, we feel that our 24/7 ${serviceType} Support Team would be better suited to support you on your wellbeing journey at this time than our ${serviceType} Coaching Team.\n\nA member of our 24/7 ${serviceType} Support Team would be glad to connect with you and provide you with in the moment support. They would also be keen to better understand your current wellbeing needs and can further review whether our Coaching Service or our Open-Ended Therapy Services would be best placed to help you meet such needs.\n\nPlease enter your phone number below to request a call-back from our 24/7 ${serviceType} support team or you can reach directly on the below numbers.`,
          buttonText: "Request Callback"
        },
        "Administer CORE-10": {
          title: "Thank you for completing the questionnaire.",
          subtitle:
            "There are just a few more questions we feel that we need to ask you. This will be the final set of questions",
          buttonText: "Proceed to Survey"
        }
      }

      return dynamicRedirectOptions;
    }
  },
  methods: {
    selectOption(option) {
      this.selectedOption = option
    },
    async getFixedSurvey(name = "WHO-5") {
      if (this.isGetSurveyLoading) return;
      try {
        this.isGetSurveyLoading = true;
        this.resetValues();
        const payload = {
          user_id: this.$store.getters.user_id,
          name
        };
        let res = await axios.post(
          config.api_env + "/application/api/u/surveys/get-fixed-survey",
          payload,
          config.options
        );
        if (res.data.success) {
          this.questionList = res.data.survey.sections[0].questions;
          this.questionsCount = res.data.survey.questions_count;
          this.surveyId = res.data.survey.id;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isGetSurveyLoading = false;
      }
    },
    async answerFixedSurvey(question_id) {
      if (this.isAnswerSurveyLoading) return;

      try {
        this.isAnswerSurveyLoading = true;
        const payload = {
          user_id: this.$store.getters.user_id,
          question_id,
          answer: [
            {
              id: this.selectedOption.id,
              answer: this.selectedOption.text
            }
          ]
        };
        const res = await axios.post(
          config.api_env +
            "/application/api/u/surveys/answer-fixed-survey-question",
          payload,
          config.options
        );
        if (res.data.success) {
          const isLastQuestion = res.data.next_question === false;
          const isResultArray = res.data.results;
          if (isLastQuestion && isResultArray) {
            this.questionnaireResult = isResultArray[0];
            if (this.questionnaireResult === "Administer CORE-10") {
              this.$emit('proceedToCORE10', 11)
            }
          }
        } else if (res.data.err && res.data.err.includes("3994")) {
          this.$store.dispatch("logout");
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isAnswerSurveyLoading = false;
      }
    },
    resetValues() {
      this.questionList = [];
      this.questionsCount = null;
      this.currentQuestionIndex = 0;
      this.selectedOption = null;
      this.answers = [];
      this.questionnaireResult = null;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    async showNextQuestion() {
      const isLastQuestion = this.questionsCount === this.currentQuestionIndex + 1;
      this.answers[this.currentQuestionIndex] = this.selectedOption;
      await this.answerFixedSurvey(this.currentQuestion.id);
      this.$emit('nextTMHQuestion')
      
      if (!isLastQuestion) {
        this.currentQuestionIndex += 1;
        this.selectedOption = this.answers[this.currentQuestionIndex] || null;
        this.scrollToTop();
      } else if (isLastQuestion && this.questionnaireResult === "Commence Coaching Program") {
        this.showSelectCoachScreen = true;
      }
    },
    showPreviousQuestion() {
      if (this.currentQuestionIndex !== 0) {
        this.$emit('previousTMHQuestion')
        this.currentQuestionIndex -= 1;
        this.selectedOption = this.answers[this.currentQuestionIndex] || null;
        this.scrollToTop();
      } else if (this.currentQuestionIndex === 0 && this.surveyId === 24) {
        this.showProceedToSurveyScreen = true;
        this.$emit('previousTMHQuestion')
      } else {
        this.previous(this.questionHistory);
      }
    },
    async proceedToNextSurvey() {
      this.$emit('nextTMHQuestion')
      this.showProceedToSurveyScreen = false;
      await this.getFixedSurvey("CORE-10");
    },
    returnToSurvey() {
      this.$emit('previousTMHQuestion')
      this.showSelectCoachScreen = false;
    }
  },
};
</script>

<style lang="less">
#TMH {
  .btn-container {
    gap: 24px;
  }

  .list-item {
    cursor: pointer;
    width: 100%;
    font-size: 1.6rem;
    line-height: 2.2rem;
    font-family: "PlusJakartaSansBold";
    border: 2px solid #aeb0bd;
    border-radius: 20px;
    padding: 24px;
    background-color: #fff;
    text-align: left;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    &.active {
      background-color: rgba(126, 108, 216, 0.05);
      border: 2px solid #7e6cd8;
    }
  }

  .dmh-start-subheading {
    white-space: pre-line;
  }

  @media only screen and (max-width: 576px) {
    .btn-container {
      gap: 16px;
    }
  }
}
</style>
